export interface TempPasswordResponseData {
  token: string;
}

export interface TempPasswordResponse {
  success: boolean;
  message?: string;
  data?: TempPasswordResponseData;
  status: number;
}

export enum MessageTypes {
  ok = 'ok',
  blocked = 'Account is blocked',
  notActivated = 'Account is not active',
  invalidPassword = 'invalidPassword',
  sessionExpired = 'sessionExpired',
  regulationsNotAccepted = 'Regulations not accepted',
  notSynced = 'Client data not synchronized'
}

export enum AlternativeLoginMessages {
  regulationsNotAccepted = 'Regulations not accepted',
  accountIsNotActive = 'Account is not active',
  accountIsBlocked = 'Account is blocked',
  badCredentials = 'Bad credentials',
  notSynced = 'Client data not synchronized'
}

export enum TokenType {
  REGISTER_ACCOUNT = 'REGISTER_ACCOUNT',
  RESET_PASSWORD = 'RESET_PASSWORD',
  UNBLOCK_ACCOUNT = 'UNBLOCK_ACCOUNT',
  ACTIVATE_ACCOUNT = 'ACTIVATE_ACCOUNT'
}

export enum TokenStatus {
  EXPIRED = 'EXPIRED',
  INVALID = 'INVALID',
  USED = 'USED',
  VALID = 'VALID'
}

export enum VerifyClientMessage {
  FAILED = 'User verification failed',
  LIMIT_REACHED = 'User verification failed - limit reached',
  UNAUTHORIZED = 'Full authentication is required to access this resource',
  EMAIL_INCORRECT = 'email: Email address is incorrect',
  PASSWORD_INCORRECT = 'Password is invalid',
  EMAIL_EMPTY = 'email: must not be empty',
  PASSWORD_EMPTY = 'password: must not be empty'
}

export enum CreateAccountMessage {
  SUCCESS = 'User create success',
  EMAIL_EXISTS = 'User create failed - email exists'
}

export enum ChangePasswordMessage {
  SAME_PASSWORDS = 'Passwords are the same',
  INVALID_OLD_PASSWORD = 'Old password is invalid'
}

export enum ResetPasswordMessage {
  PERSON_ID_INCORRECT = 'Given PERSON ID is incorrect',
  PESEL_LIMIT_REACHED = 'User verification failed - limit reached'
}

export enum GetDocumentMessage {
  NO_DOCUMENT_AVAILABLE = 'No document to download on given agreement for given user'
}
export interface CompleteActivationAccountCredentials {
  password: string;
  token: string;
  peselFourLastDigits: string;
  regulationsAccepted: true;
}

export type KisaUserStatus = AlternativeLoginMessages | string | null;

export type UserStatus =
  | MessageTypes.blocked
  | MessageTypes.notActivated
  | MessageTypes.invalidPassword
  | MessageTypes
  | string
  | null;

export interface UserCredentials {
  login: string;
  password: string;
  conditionsAccepted?: boolean;
}

export type UserLoginOrPhoneLoginType = 'login' | 'phoneNumber';

export type AlternativeCredentialsWithEmail<
  T extends UserLoginOrPhoneLoginType
> = AlternativeCredentials<T> & {
  email: string;
  regulationsAccepted: boolean;
};

export type AlternativeCredentials<T extends UserLoginOrPhoneLoginType> =
  T extends 'login'
    ? { login: string; password: string; regulationsAccepted?: boolean }
    : { phoneNumber: string; password: string; regulationsAccepted?: boolean };

export interface UserData {
  email: string;
  firstName: string;
  lastName: string;
}
export interface UserPayload {
  data: UserDeprecated;
}

export interface UserLoginData {
  token: string;
  type: string;
  email?: string;
}

export type UserCompleteActivateAccountMessages =
  | 'Given PERSON ID is incorrect'
  | 'User verification failed - limit reached';

export interface UserCompleteActivateAccountResponse {
  data?: UserLoginData;
  status: number;
  message?: UserCompleteActivateAccountMessages;
  success: boolean;
}

export interface UserLoginResponse {
  data?: UserLoginData;
  status: number;
  message?: string;
  success: boolean;
}

/**
 * New entity enclosed in response from '/client' endpoint
 */
export interface Client extends Omit<UserDeprecated, 'id' | 'peselGender'> {
  clientId: number;
  firstName: string;
  firstNameVocative: string;
  surname: string;
  gender: 'M' | 'F';
  userAccountEmail: string;
  showContactDetailsAcceptance: boolean;
  showUserAccountActivatedMessage: boolean;
  synchronizationTime: string;
}

/**
 * @deprecated in mid transition use NewUser instead
 */
export interface UserDeprecated {
  id: number | null;
  language: string;
  firstName: string;
  firstNameVocative: string;
  lastName: string;
  email: string;
  peselGender: 'M' | 'F';
  token: string | null;
  userStatus: UserStatus;
  kisaUserStatus: KisaUserStatus;
  regulationsAcceptanceRequired: boolean;
  showContactDetailsAcceptance: boolean;
  impersonation: boolean;
  showUserAccountActivatedMessage: boolean;
}

export interface UserEmail {
  email: string;
}

export type UserEmailAndLoginOrPhone =
  | {
      login: string;
      phoneNumber: never;
      email: string;
    }
  | { login: never; phoneNumber: string; email: string };

export interface UserPassword {
  password: string;
}

export interface UnblockAccount {
  password: string;
  personId: string;
}

export interface ResetUserPassword {
  personId: string;
  password: string;
  conditionsAccepted: boolean;
}

export interface ResetEmailResponse {
  status: number;
}

export interface ResetPassword extends UserPassword {
  token: string;
  personId: string;
}

export interface ChangePassword {
  oldPassword: string;
  newPassword: string;
}

export interface ActivateAccount {
  token: string;
}
export interface ResendActivateAccount {
  activationToken: string;
}
export interface ActivateAccountResponse {
  status: number;
}

export interface ResetPasswordResponse {
  status: number;
  success?: boolean;
  message?: string;
}

export interface ChangePasswordResponse {
  status: number;
  message?: string;
  success?: boolean;
}

export interface ValidateToken {
  token: string;
  type: TokenType;
}

export interface ValidateTokenData {
  status: TokenStatus;
  login: string | null;
}

export interface ValidateTokenResponse {
  success: boolean;
  data: ValidateTokenData;
}

export interface UnblockAccountResponse {
  status: number;
}

export interface VerifyClient {
  sourceContractId: string;
  personId: string;
}

export interface VerifyClientResponse {
  success?: boolean;
  message?: string;
  status: number;
}

export interface CreateAccount {
  email: string;
  password: string;
}

export interface CreateAccountFromACP {
  email: string;
  password: string;
}

export interface CreateAccountFromACPResponse {
  message?: CreateAccountMessage | VerifyClientMessage;
  status: number;
  success?: boolean;
}

export interface CreateAccountResponse {
  message?: CreateAccountMessage | VerifyClientMessage;
  status: number;
  success?: boolean;
}

export interface ContactFormPayload {
  email: string;
  phone: string;
  message: string;
}

export type User = UserDeprecated;
export type UserState = UserDeprecated;

export interface UserResponse<T> {
  data: T;
  success: boolean;
}

export enum AddressTypePL {
  CONSTANT = 'CONSTANT',
  CORRESPONDENCE = 'CORRESPONDENCE',
  TEMPORARY = 'TEMPORARY'
}

export enum AddressTypeRO {
  MAIN = 'MAIN',
  CORRESPONDENCE = 'CORRESPONDENCE',
  OTHER = 'OTHER'
}

export type AddressType = AddressTypePL | AddressTypeRO;

export interface BaseAddress {
  addressId: number;
  addressType: AddressType | null;
  apartmentNumber: string | null;
  buildingNumber: string | null;
  street: string | null;
  city: string | null;
  countryCode: string | null;
  country: string | null;
}

export interface UserAddress extends BaseAddress {
  postalCode: string | null;
  postCity: string | null;
}

export interface AddressPL extends UserAddress {
  kind: 'PL';
}

export interface AddressRO extends BaseAddress {
  kind: 'RO';
  county: string | null;
  entrance: string | null;
  floor: string | null;
  streetNumber: string | null;
}

export type Address = AddressPL | AddressRO;

export interface AddressesData {
  addresses: UserAddress[];
}

export enum EmailType {
  PRIVATE = 'PRIVATE',
  BUSINESS = 'BUSINESS',
  COURTESY = 'COURTESY',
  UNKNOWN = 'UNKNOWN'
}

export interface Email {
  emailAddressId: number;
  emailAddress: string | null;
  emailAddressType: EmailType | null;
  loginEmail: boolean | null;
}

export interface EmailsData {
  emails: Email[];
}

export interface AddEmailAddressPayload {
  emailAddress: string;
  emailAddressType: EmailType;
}

export interface EditEmailAddressPayload extends AddEmailAddressPayload {
  emailAddressId: number;
}

export interface UpdateTaxOfficePayload {
  agreementId: number;
  taxOfficeId: number;
}

export interface EmailResponse {
  success?: boolean;
  status: number;
}

export enum PhoneNumberType {
  MOBILE = 'MOBILE',
  HOME = 'HOME'
}
export const PolishPhoneCountryCode = 'POLAND';
export const RomanianPhonePrefix = '+40';

export type InconsistentPhoneInterface = Omit<
  Phone,
  'phoneNumberCountryCode'
> & {
  phoneNumberCodeCountry: string | null;
};
export interface Phone {
  phoneNumberId: number;
  phoneNumber: string | null;
  phoneNumberCodePrefix: string | null;
  phoneNumberCountryCode: string | null;
  phoneNumberType: PhoneNumberType | null;
}
export interface CreatePhone {
  phoneNumberCountryCode: string;
  phoneNumberType: PhoneNumberType;
  phoneNumber: string;
  phonePrefix?: string;
}

export interface UpdatePhone extends CreatePhone {
  phoneNumberId: number;
}

export interface DeletePhone {
  phoneNumberId: number;
  phoneNumber: string;
  phoneNumberType: string;
  phoneNumberCountryCode: string;
}

export interface PhoneResponse {
  status: number;
  success?: boolean;
}
export interface InconsistentPhoneData {
  phones: InconsistentPhoneInterface[];
}
export interface PhonesData {
  phones: Phone[];
}

export interface UserInfo {
  identityNumber: string;
}

export interface BaseCreateAddress {
  buildingNumber: string;
  addressType: AddressType;
  city: string;
  countryCode: string;
  flatNumber?: string;
  street: string;
}

export interface UserCreateAddress extends BaseCreateAddress {
  zipCode: string;
  postName?: string;
}

export interface CreatePlAddress extends UserCreateAddress {
  kind: 'PL';
}

export interface CreateRoAddress extends BaseCreateAddress {
  kind: 'RO';
  county: string;
  entrance: string;
  floor: string;
  streetNumber: string;
}

export type CreateAddress = CreatePlAddress | CreateRoAddress;

export interface DeleteAddress {
  addressId: number;
}

export interface AddressResponse {
  success: boolean;
  status: number;
}

export interface UpdatePlAddress extends CreatePlAddress {
  addressId: number;
}

export interface UpdateRoAddress extends CreateRoAddress {
  addressId: number;
}

export type UpdateAddress = UpdatePlAddress | UpdateRoAddress;

export interface UserAcceptanceDataResponse {
  data: number;
  success: boolean;
}
